import type { BoxProps } from "@chakra-ui/react";

import * as React from "react";
import type { ReactNode } from "react";

import { Button } from "@shared/ui/chakra/buttons/button";
import { Icon } from "@shared/ui/chakra/data-display/icon/icon.tsx";
import { CellBody } from "@shared/ui/molecules/cell-body/cell-body";

type Props = {
  asChild?: boolean;
  caption?: string;
  captionColor?: string;
  captionDotColor?: string;
  captionWithDot?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  helpCaption?: string;
  helpCaptionColor?: string;
  helpCaptionFull?: string;
  helpIcon?: ReactNode;
  helpOverline?: string;
  helpText?: ReactNode;
  helpTextColor?: string;
  helpTextFull?: string;
  href?: string;
  icon?: ReactNode;
  iconAlign?: BoxProps["alignSelf"];
  lineClampBody?: number;
  overline?: ReactNode;
  withChevron?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export const Cell = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      asChild,
      caption,
      captionColor,
      captionDotColor,
      captionWithDot,
      children,
      disabled,
      helpCaption,
      helpCaptionColor,
      helpCaptionFull,
      helpIcon,
      helpOverline,
      helpText,
      helpTextColor,
      helpTextFull,
      href,
      icon,
      iconAlign,
      lineClampBody = 1,
      overline,
      withChevron = true,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (href && (event.metaKey || event.ctrlKey || event.shiftKey)) {
        return;
      }
      if (href) {
        event.preventDefault();
      }
      onClick?.(event);
    };

    return (
      <Button
        alignItems="center"
        as={href ? "a" : undefined}
        asChild={asChild}
        cursor={onClick || href ? "pointer" : "default"}
        disabled={disabled}
        gap="3"
        h="auto"
        justifyContent="space-between"
        outline="none"
        px="4"
        py="3"
        ref={ref}
        variant="plain"
        w="full"
        onClick={handleClick}
        {...(href && { href })}
        {...props}
      >
        <CellBody
          body={children}
          caption={caption}
          captionColor={captionColor}
          captionDotColor={captionDotColor}
          captionWithDot={captionWithDot}
          icon={icon}
          iconAlign={iconAlign}
          lineClampBody={lineClampBody}
          overline={overline}
          type="leading"
          isSubtle={disabled}
        />
        {(helpText || helpIcon) && (
          <CellBody
            body={helpText || ""}
            bodyColor={helpTextColor}
            bodyFull={helpTextFull}
            caption={helpCaption}
            captionColor={helpCaptionColor}
            captionFull={helpCaptionFull}
            icon={helpIcon}
            overline={helpOverline}
            type="trailing"
            isSubtle={disabled}
          />
        )}
        {withChevron && (
          <Icon color="gray.300" h="24px" name="CellChevronForward" w="8px" />
        )}
      </Button>
    );
  },
);
